import type { ConnectorFeatureModel } from '@/adapters/apiv1.g';
import { normalizeOptions } from '@/adapters/shared/normalize-options';
import type { ConnectorFeature } from '@/types/core/connector';
import { isNilUUID, type UUID } from '@/utils/uuid';

/**
 * Maps the backend's slot-name → secret-id map into a typed record, dropping
 * empty and NIL-UUID placeholder entries (the latter represent a cleared
 * binding) so secretIds only ever holds real bindings. Returns undefined when
 * no slots are bound.
 */
function extractSecretIds(
  secretIds: Record<string, string> | undefined
): Record<string, UUID> | undefined {
  if (!secretIds) return undefined;
  const entries = Object.entries(secretIds).filter(
    ([, value]) => !!value && !isNilUUID(value as UUID)
  );
  if (entries.length === 0) return undefined;
  return Object.fromEntries(
    entries.map(([slot, value]) => [slot, value as UUID])
  );
}

/**
 * Transform a backend ConnectorFeatureModel to a frontend ConnectorFeature type.
 */
export function extractConnectorFeature(
  model: ConnectorFeatureModel
): ConnectorFeature {
  return {
    _rev: model._rev,
    connectorId: (model.connector_id ?? '') as UUID,
    featureId: (model.feature_id ?? '') as UUID,
    name: model.feature_name ?? '',
    description: '',
    options: normalizeOptions(model.options),
    requirements: model.requirements ?? [],
    status: model.enabled ?? false,
    providerName: model.provider_name,
    spaces: model.spaces ?? [],
    secretIds: extractSecretIds(model.secret_ids),
    clientIds: (model.client_ids ?? []) as UUID[],
    type: model.type,
    schedulable: model.schedulable,
    payloadSchema: model.payload_schema ?? undefined,
  };
}
